import React, { useEffect, useMemo } from 'react';
import { Card, Bubble, Row, Col } from 'tea-component';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import OverviewCard, { ISpecialJumpParams } from '@/pages/intelligence/components/OverviewCard';
import classnames from 'classnames';
import { COMPETITOR_GUIDE_CLASSNAMES } from '@intelligence/components/UserGuide/Competitor';
import Loading from '@/pages/intelligence/components/Loading';
import NoData from '@/pages/intelligence/components/NoData';
import { logDataBrainCardClick } from '../../utils/loggingUtils';

interface IDataBrainKPIItem {
  titleContent: React.ReactNode;
  titleIcon: string;
  baseIndicators: {
    indicatorsVal: string | number;
    latestDate: string;
  };
  numArrowData: {
    numContent: string;
    numSize: 'big' | 'small' | 'zero' | 'new';
    numColor: 'successColor' | 'errorColor' | 'grayColor';
    arrow: boolean;
    arrowColor: 'trendUp' | 'trendDown' | 'grayUp' | 'grayDown';
  };
  lineData: {
    xAxisData: string[];
    seriesData: number[];
  };
  rankData?: unknown;
  endRateDateTime: string;
  endTwoRateDateTime: string;
  endRateValue: number;
  endTwoRateValue: number;
  metricUserGuide?: string;
}

interface IDataBrainCalibrationContentProps {
  entityType?: any;
  gameId: any;
  gameName?: string;
  moduleName?: any;
  conditionMarket?: any;
  isOperateRegion?: any;
  setActiveId?: any;
  setActiveSegment?: any;
  activeId?: any;
  getDatabrainRevenueUnits?: any;
  databrainRevenueUnitsLoading?: any;
  databrainRevenueUnitsDatas?: any;
}

const query: IRecord = {
  isDataBrainTrueDataMode: true,
};

const specialJumpParams: ISpecialJumpParams = {
  conditionDate: 'day',
};

const DataBrainCalibrationContent = (props: IDataBrainCalibrationContentProps) => {
  const {
    entityType,
    gameId,
    gameName,
    moduleName,
    conditionMarket,
    isOperateRegion,
    setActiveId,
    setActiveSegment,
    activeId,
    getDatabrainRevenueUnits,
    databrainRevenueUnitsLoading,
    databrainRevenueUnitsDatas,
  } = props;
  const { formatMessage } = useIntlExtend();

  // fixed to lifetime for now; can extend with selector later
  const DEFAULT_LIFETIME = 'lifetime';
  // widen to string to allow comparisons with other date types (e.g., 'last month') without TS literal mismatch
  const steamDateType: string = DEFAULT_LIFETIME;

  useEffect(() => {
    // initial fetch
    getDatabrainRevenueUnits(DEFAULT_LIFETIME);
  }, []);

  const conditionDateCur = useMemo(() => {
    return steamDateType === 'last month' ? 'day' : 'month';
  }, [steamDateType]);

  return (
    <Card className="dbi-performance-summary dbi-kpioverview-card">
      <Card.Body
        title={
          <div className="performance-felx">
            <Bubble
              content={
                <div>
                  <div>
                    <img
                      className="platform-title-icon"
                      style={{ height: '20px', width: '20px', margin: '0 4px', verticalAlign: 'middle', marginLeft: '-2px' }}
                      src={getDataSourceIcon('ogdb')}
                    />
                    {formatMessage({ id: 'dbi_combine_databrain_data_tips_title' })}
                  </div>
                  <div>{formatMessage({ id: 'dbi_combine_databrain_data_tips_desc' })}</div>
                  <div>{formatMessage({ id: 'dbi_combine_databrain_data_tips_update' })}</div>
                </div>
              }
            >
              <div className="performance-felx">
                <img src={getDataSourceIcon('ogdb')} className="dbi-pcGame-select-icon" style={{ height: '20px', width: '20px' }} />
              </div>
            </Bubble>
            <span className="performance-felx-text">{formatMessage({ id: 'dbi_combine_databrain_data_tips_title', defaultMessage: 'DataBrain Calibration Data' })}</span>
          </div>
        }
      >
        <Row>
          {databrainRevenueUnitsLoading && (
            <Loading content={formatMessage({ id: 'common_loading' })} style={{ height: 142, color: '#444' }} />
          )}
          {!databrainRevenueUnitsLoading && databrainRevenueUnitsDatas?.length === 0 && <NoData />}
          {!databrainRevenueUnitsLoading && databrainRevenueUnitsDatas?.length > 0 && (
            databrainRevenueUnitsDatas.map((item: any, index: number) => {
              const cardProps: any = {
                key: `indicator-${index}`,
                className: classnames({
                  [COMPETITOR_GUIDE_CLASSNAMES.ELEMENT4]: item?.metricUserGuide === 'digitalRevenue',
                }),
                titleContent: item.titleContent,
                titleIcon: item.titleIcon,
                baseIndicators: item.baseIndicators,
                numArrowData: item.numArrowData,
                lineData: item.lineData,
                rankData: item.rankData,
                index,
                moduleName,
                entityType,
                setActiveId,
                conditionDate: conditionDateCur,
                conditionMarket,
                gameId,
                gameName,
                activeId,
                cardItem: item,
                setActiveSegment,
                isOperateRegion,
                isShowDataSourceIcon: false,
                isShowRateTips: true,
                data_source: 'ogdb',
                // hide rank for DataBrain calibration cards
                isShowRank: false,
              };
              return (
                <Col span={12} key={index}>
                  <OverviewCard {...cardProps} query={query} specialJumpParams={specialJumpParams} burriedPointLogging={logDataBrainCardClick}/>
                </Col>
              );
            })
          )}
        </Row>
      </Card.Body>
    </Card>
  );
};

export default DataBrainCalibrationContent;
